import { api } from '@/lib/api/client'
import type { Division, PortfolioSummary, Project } from '@/types/domain'
import { fetchAll } from '@/lib/api/paged'

interface Collection<T> {
  data: T[]
  meta?: { total?: number }
}

export async function fetchProjects(stage?: string): Promise<Project[]> {
  return fetchAll<Project>('/projects', stage === undefined ? {} : { stage })
}

export async function fetchDivisions(): Promise<Division[]> {
  const { data } = await api.get<Collection<Division>>('/divisions')

  return data.data
}

export async function fetchSummary(): Promise<PortfolioSummary> {
  const { data } = await api.get<{ data: PortfolioSummary }>('/portfolio/summary')

  return data.data
}

/**
 * The key is deliberately not sent. It comes out of the division's own
 * sequence, so a caller naming one is how two projects both become AIS-47.
 */
export async function createProject(input: {
  divisionId: string
  name: string
  clientId?: string | null
}): Promise<Project> {
  const { data } = await api.post<{ data: Project }>('/projects', input)

  return data.data
}

export interface ProjectChanges {
  name?: string
  clientId?: string | null
  leadUserId?: string | null
  description?: string | null
  solution?: string | null
  startDate?: string | null
  targetDate?: string | null
  valueOmr?: string | null
  budgetOmr?: string | null
}

export async function updateProject(key: string, changes: ProjectChanges): Promise<Project> {
  const { data } = await api.patch<{ data: Project }>(`/projects/${key}`, changes)

  return data.data
}

/**
 * The engagement stage — the pipeline the portfolio charts. Separate from the
 * rest because it is a move through a workflow, not a field edit.
 */
export async function setProjectStage(key: string, stageId: string): Promise<Project> {
  const { data } = await api.patch<{ data: Project }>(`/projects/${key}/stage`, { stageId })

  return data.data
}

/** Projects archive; they are never deleted. Every reference to AIS-14 outlives it. */
export async function archiveProject(key: string, archived: boolean): Promise<Project> {
  const { data } = await api.post<{ data: Project }>(
    `/projects/${key}/${archived ? 'archive' : 'unarchive'}`,
    {},
  )

  return data.data
}
